/**
 * Shared types for DeviceSpecifications component
 * Extracted from DeviceSpecifications.tsx for reuse across modules
 */

export enum Question {
  Storage = 'storage',
  NetworkType = 'networkType',
  Battery = 'battery',
  Condition = 'condition',
  // iPhone specific questions
  Functionality = 'functionality',
  ScreenCondition = 'screenCondition',
  HousingCondition = 'housingCondition',
  BatteryCapacity = 'batteryCapacity',
}

// Multilingual label object (as stored in Supabase JSONB)
export type MultilingualLabel = Record<string, string>;

// Dynamic questions & options (loaded from Supabase)
export type DynamicQuestionOption = {
  id: string;
  optionKey: string;
  label: string;  // Current language label for display
  labelRaw?: MultilingualLabel;  // Raw JSONB with all translations (for backend/calendar)
  description?: string | null;
  descriptionRaw?: MultilingualLabel | null;  // Raw JSONB for description (for re-resolve on language change)
  iconName?: string | null;
  priceEffectType: 'none' | 'fixed' | 'percent' | 'multiplier' | 'special';
  priceEffectValue: number | null;
};

export type DynamicQuestion = {
  id: string;
  questionKey: string;
  label: string;  // Current language label for display
  labelRaw?: MultilingualLabel;  // Raw JSONB with all translations (for backend/calendar)
  subtitle?: string | null;
  helpText?: string | null;
  isRequired: boolean;
  orderIndex: number;
  options: DynamicQuestionOption[];
};

export interface DeviceSpecificationsProps {
  deviceType: string;
  modelName: string;
  modelKey?: string; // Optional for backward compatibility during refactor, but preferred
  /** Customer-facing name from device_models.display_name, known at selection time. */
  modelDisplayName?: string;
  onSellClick: (specs: { 
    storage: string; 
    networkType?: string; 
    battery?: string; 
    condition?: string; 
    // iPhone specific properties
    functionality?: string;
    screenCondition?: string;
    housingCondition?: string;
    batteryCapacity?: string;
    price: number;
    imageSrc: string;
    displayName?: string;
  }) => void;
}

// Dynamic spec item for displaying user's answers in cart/checkout
// Stores both Dutch (for backend/calendar) and current language (for frontend display)
export type DynamicSpecItem = {
  questionKey: string;
  optionKey: string;
  // Dutch labels - used for backend logs and calendar (always in Dutch)
  question_nl: string;
  option_nl: string;
  // Current language labels - used for frontend display (dynamic based on user's language)
  question_label: string;
  option_label: string;
  // Full multilingual JSONB - used to re-resolve labels when language changes
  question_labels?: Record<string, string>;
  option_labels?: Record<string, string>;
};

export type SellSpecs = {
  storage: string;
  networkType?: string;
  battery?: string;
  condition?: string;
  functionality?: string;
  screenCondition?: string;
  housingCondition?: string;
  batteryCapacity?: string;
  price: number;
  imageSrc: string;
  /** Customer-facing model name (from device_models.display_name) for cart, checkout and e-mail. */
  displayName?: string;
  // Dynamic specs array containing all answered questions with their labels
  dynamicSpecs?: DynamicSpecItem[];
  // Flag for "On Request" items that don't have a fixed price
  isOnRequest?: boolean;
};

